// src/hooks/useVisibilityRefresh.js
// ─────────────────────────────────────────────────────────────────────────────
// Fires `callback` every time the browser tab becomes visible again.
// Prevents stale data and expired sessions after the tab has been in the
// background (throttled timers, suspended WebSocket, expired JWT).
//
// Usage:
//   useVisibilityRefresh(loadData);          // re-fetch when tab refocuses
//   useVisibilityRefresh(loadData, 30000);   // only if hidden for >30s
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from 'react';

export function useVisibilityRefresh(callback, minHiddenMs = 10000) {
  const hiddenAt    = useRef(null);
  const callbackRef = useRef(callback);

  // Always keep callbackRef current so the effect never needs to re-run
  // when the callback changes (avoids re-registering the listener)
  useEffect(() => { callbackRef.current = callback; }, [callback]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        const wasHiddenMs = hiddenAt.current ? Date.now() - hiddenAt.current : 0;
        if (wasHiddenMs >= minHiddenMs) {
          callbackRef.current();
        }
        hiddenAt.current = null;
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [minHiddenMs]); // only minHiddenMs as dep — listener registered once
}

export default useVisibilityRefresh;
